import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import Header from './components/Header';
import Footer from './components/Footer';
import { AdminAuthProvider, useAdminAuth } from './context/AdminAuthContext';
import styles from './App.module.css';

// Lazy-load every page so Firebase only downloads when the admin portal is visited
const Landing        = lazy(() => import('./pages/Landing'));
const Docs           = lazy(() => import('./pages/Docs'));
const Privacy        = lazy(() => import('./pages/Privacy'));
const Terms          = lazy(() => import('./pages/Terms'));
const AdminLogin     = lazy(() => import('./pages/admin/AdminLogin'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminGolfers   = lazy(() => import('./pages/admin/AdminGolfers'));
const AdminSeasons       = lazy(() => import('./pages/admin/AdminSeasons'));
const AdminSeasonDetail  = lazy(() => import('./pages/admin/AdminSeasonDetail'));
const AdminManageAdmins = lazy(() => import('./pages/admin/AdminManageAdmins'));
const AdminOrgSettings  = lazy(() => import('./pages/admin/AdminOrgSettings'));
const AdminHelp         = lazy(() => import('./pages/admin/AdminHelp'));
const AdminEvent        = lazy(() => import('./pages/admin/AdminEvent'));
const AdminEventNew     = lazy(() => import('./pages/admin/AdminEventNew'));
const AdminScores       = lazy(() => import('./pages/admin/AdminScores'));
const LiveLeaderboard   = lazy(() => import('./pages/LiveLeaderboard'));
const AdminPlayerStats  = lazy(() => import('./pages/admin/AdminPlayerStats'));

function PageSpinner() {
  return (
    <div className={styles.spinnerWrap}>
      <div className={styles.spinner} />
    </div>
  );
}

// Public layout wraps non-admin pages
function PublicLayout({ children }) {
  return (
    <div className={styles.publicLayout}>
      <Header />
      <div className={styles.publicContent}>{children}</div>
      <Footer />
    </div>
  );
}

/**
 * Wraps all protected admin routes. Auth resolves once here — navigating
 * between admin pages reads from context instantly, no Firestore round-trip,
 * no blank flash.
 */
function ProtectedAdminRoute({ children }) {
  const { adminUser, loading } = useAdminAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !adminUser) navigate('/admin/login', { replace: true });
  }, [loading, adminUser, navigate]);

  if (loading) return <PageSpinner />;
  if (!adminUser) return null;
  return children;
}

export default function App() {
  return (
    <AdminAuthProvider>
      <Suspense fallback={<PageSpinner />}>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<PublicLayout><Landing /></PublicLayout>} />
          <Route path="/docs"    element={<PublicLayout><Docs /></PublicLayout>} />
          <Route path="/privacy" element={<PublicLayout><Privacy /></PublicLayout>} />
          <Route path="/terms"   element={<PublicLayout><Terms /></PublicLayout>} />

          {/* Live leaderboard — no header/footer, no auth */}
          <Route path="/live/:code" element={<LiveLeaderboard />} />

          {/* Admin routes — no public header/footer */}
          <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
          <Route path="/admin/login" element={<AdminLogin />} />

          <Route path="/admin/dashboard" element={<ProtectedAdminRoute><AdminDashboard /></ProtectedAdminRoute>} />
          <Route path="/admin/golfers"   element={<ProtectedAdminRoute><AdminGolfers /></ProtectedAdminRoute>} />
          <Route path="/admin/seasons"           element={<ProtectedAdminRoute><AdminSeasons /></ProtectedAdminRoute>} />
          <Route path="/admin/seasons/:id"      element={<ProtectedAdminRoute><AdminSeasonDetail /></ProtectedAdminRoute>} />
          <Route path="/admin/admins"    element={<ProtectedAdminRoute><AdminManageAdmins /></ProtectedAdminRoute>} />
          <Route path="/admin/settings"  element={<ProtectedAdminRoute><AdminOrgSettings /></ProtectedAdminRoute>} />
          <Route path="/admin/help"      element={<ProtectedAdminRoute><AdminHelp /></ProtectedAdminRoute>} />
          <Route path="/admin/events/new"   element={<ProtectedAdminRoute><AdminEventNew /></ProtectedAdminRoute>} />
          <Route path="/admin/events/:id"        element={<ProtectedAdminRoute><AdminEvent /></ProtectedAdminRoute>} />
          <Route path="/admin/events/:id/scores" element={<ProtectedAdminRoute><AdminScores /></ProtectedAdminRoute>} />
          <Route path="/admin/golfers/:id/stats" element={<ProtectedAdminRoute><AdminPlayerStats /></ProtectedAdminRoute>} />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </AdminAuthProvider>
  );
}
